if (!customElements.get('product-modal')) {
  // Product media modal carousel v1.2.0.
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();

      this.content = this.querySelector('.product-media-modal__content');
      this.previousButton = this.querySelector('.product-media-modal__navigation--previous');
      this.nextButton = this.querySelector('.product-media-modal__navigation--next');
      this.counter = this.querySelector('.product-media-modal__counter');
      this.currentCounter = this.querySelector('[data-product-modal-current]');
      this.totalCounter = this.querySelector('[data-product-modal-total]');
      this.mediaItems = [];
      this.currentIndex = 0;
      this.scrollFrame = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.visualViewport = window.visualViewport;
      this.syncViewportHeight = this.syncViewportHeight.bind(this);

      [this.previousButton, this.nextButton].forEach((button) => {
        button?.addEventListener('pointerup', (event) => event.stopPropagation());
      });
      this.previousButton?.addEventListener('click', () => this.navigate(-1));
      this.nextButton?.addEventListener('click', () => this.navigate(1));
      this.content?.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
      this.content?.addEventListener('wheel', this.handleWheel.bind(this), { passive: false });
      this.addEventListener('keydown', this.handleKeydown.bind(this));
      this.visualViewport?.addEventListener('resize', this.syncViewportHeight, { passive: true });
      window.addEventListener('orientationchange', this.syncViewportHeight, { passive: true });

      if (this.content && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.hasAttribute('open')) this.scrollToIndex(this.currentIndex, 'auto');
        });
        this.resizeObserver.observe(this.content);
      }
    }

    hide() {
      document.body.classList.remove('product-media-modal-open');
      super.hide();
    }

    show(opener) {
      this.syncViewportHeight();
      super.show(opener);
      document.body.classList.add('product-media-modal-open');
      this.showActiveMedia();
    }

    syncViewportHeight() {
      const viewportHeight = this.visualViewport?.height || window.innerHeight;

      if (!Number.isFinite(viewportHeight) || viewportHeight <= 0) return;

      this.style.setProperty('--eb-product-modal-viewport-height', `${Math.round(viewportHeight)}px`);
    }

    showActiveMedia() {
      this.syncMobileVariantMedia();
      const mediaId = this.openedBy.getAttribute('data-media-id');
      const activeMedia = this.querySelector(`[data-media-id="${mediaId}"]`);

      if (!activeMedia || !this.content) return;

      this.querySelectorAll('[data-media-id]').forEach((element) => element.classList.remove('active'));
      this.mediaItems = Array.from(this.content.children).filter((element) => {
        if (!element.hasAttribute('data-media-id')) return false;
        if (element === activeMedia) return true;
        return !element.hidden && element.style.display !== 'none' && getComputedStyle(element).display !== 'none';
      });

      this.currentIndex = Math.max(0, this.mediaItems.indexOf(activeMedia));
      const activeMediaTemplate = activeMedia.querySelector('template');
      const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

      this.updateActiveState(this.currentIndex);
      requestAnimationFrame(() => this.scrollToIndex(this.currentIndex, 'auto'));

      if (activeMedia.nodeName == 'DEFERRED-MEDIA' && activeMediaContent && activeMediaContent.querySelector('.js-youtube'))
        activeMedia.loadContent();
    }

    syncMobileVariantMedia() {
      if (!this.content) return;

      const sectionId = this.id.replace('ProductModal-', '');
      const gallery = document.querySelector(`#MediaGallery-${CSS.escape(sectionId)}:not([id$='-duplicate'])`)
        || document.querySelector(`[id^="MediaGallery-${CSS.escape(sectionId)}"]`);
      const boundaries = gallery?.variantMediaBoundaries || [];
      const selectedVariantId = gallery?.dataset.ebSelectedVariantId;
      const selectedBoundaryIndex = boundaries.findIndex(
        (boundary) => String(boundary.id) === String(selectedVariantId)
      );
      const selectedBoundary = boundaries[selectedBoundaryIndex];
      const nextBoundary = boundaries[selectedBoundaryIndex + 1];
      const modalMedia = Array.from(this.content.querySelectorAll(':scope > [data-eb-media-position]'));
      const galleryOwnsVariantVisibility = Boolean(
        gallery?.querySelector('[data-eb-active-variant-media="true"]')
      );
      const canFilter = (window.matchMedia('(max-width: 749px)').matches || galleryOwnsVariantVisibility)
        && boundaries.length > 1
        && boundaries[0].position === 1
        && selectedBoundaryIndex >= 0
        && selectedBoundary.position > 0;

      modalMedia.forEach((mediaItem) => {
        if (!canFilter) {
          if (mediaItem.dataset.ebMobileHidden === 'true') mediaItem.hidden = false;
          if (mediaItem.dataset.ebModalOriginalDisplay !== undefined) {
            mediaItem.style.display = mediaItem.dataset.ebModalOriginalDisplay;
            delete mediaItem.dataset.ebModalOriginalDisplay;
          }
          delete mediaItem.dataset.ebMobileHidden;
          return;
        }

        const position = Number(mediaItem.dataset.ebMediaPosition);
        const belongsToVariant = position >= selectedBoundary.position
          && (!nextBoundary || position < nextBoundary.position);
        mediaItem.hidden = !belongsToVariant;
        mediaItem.dataset.ebMobileHidden = String(!belongsToVariant);
        if (mediaItem.dataset.ebModalOriginalDisplay === undefined) {
          mediaItem.dataset.ebModalOriginalDisplay = mediaItem.style.display;
        }
        mediaItem.style.display = belongsToVariant ? 'block' : 'none';
      });
    }

    navigate(direction, behavior = this.reducedMotion.matches ? 'auto' : 'smooth') {
      const targetIndex = Math.min(Math.max(this.currentIndex + direction, 0), this.mediaItems.length - 1);

      if (targetIndex === this.currentIndex) return;

      window.pauseAllMedia();
      this.currentIndex = targetIndex;
      this.updateActiveState(targetIndex);
      this.scrollToIndex(targetIndex, behavior);
    }

    scrollToIndex(index, behavior = 'auto') {
      const firstMedia = this.mediaItems[0];
      const targetMedia = this.mediaItems[index];

      if (!firstMedia || !targetMedia || !this.content) return;

      this.content.scrollTo({
        left: targetMedia.offsetLeft - firstMedia.offsetLeft,
        behavior,
      });
    }

    updateActiveState(index) {
      const hasMultipleMedia = this.mediaItems.length > 1;

      this.mediaItems.forEach((element, mediaIndex) => {
        const isActive = mediaIndex === index;
        element.classList.toggle('active', isActive);
        element.setAttribute('aria-hidden', String(!isActive));
        element.setAttribute('role', 'group');
        element.setAttribute('aria-label', `${mediaIndex + 1} / ${this.mediaItems.length}`);
      });

      if (this.currentCounter) this.currentCounter.textContent = index + 1;
      if (this.totalCounter) this.totalCounter.textContent = this.mediaItems.length;
      if (this.counter) this.counter.hidden = !hasMultipleMedia;
      if (this.previousButton) {
        this.previousButton.hidden = !hasMultipleMedia;
        this.previousButton.disabled = index === 0;
      }
      if (this.nextButton) {
        this.nextButton.hidden = !hasMultipleMedia;
        this.nextButton.disabled = index === this.mediaItems.length - 1;
      }
    }

    handleScroll() {
      if (this.scrollFrame || this.mediaItems.length < 2) return;

      this.scrollFrame = requestAnimationFrame(() => {
        const firstOffset = this.mediaItems[0].offsetLeft;
        const nextIndex = this.mediaItems.reduce((closestIndex, element, index) => {
          const currentDistance = Math.abs(this.content.scrollLeft - (element.offsetLeft - firstOffset));
          const closestDistance = Math.abs(
            this.content.scrollLeft - (this.mediaItems[closestIndex].offsetLeft - firstOffset)
          );
          return currentDistance < closestDistance ? index : closestIndex;
        }, 0);

        if (nextIndex !== this.currentIndex) {
          window.pauseAllMedia();
          this.currentIndex = nextIndex;
          this.updateActiveState(nextIndex);
        }

        this.scrollFrame = null;
      });
    }

    handleWheel(event) {
      if (!this.hasAttribute('open') || this.mediaItems.length < 2) return;
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

      event.preventDefault();
      this.content.scrollLeft += event.deltaY;
    }

    handleKeydown(event) {
      if (!this.hasAttribute('open') || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.code !== 'ArrowLeft' && event.code !== 'ArrowRight') return;

      event.preventDefault();
      this.navigate(event.code === 'ArrowRight' ? 1 : -1, 'auto');
    }
  });
}
